import React, { useMemo } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
} from '@mui/material';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel';
import { useNavigate } from 'react-router-dom';
import { SalesRecordingStatus, SalesRecordingType } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { formatDateSold, salesRecordingTypeLabel } from '../../../utils/sales';

type Props = {
  sales: SalesRecording[],
  totalSales: string | number | undefined,
  totalCommission: string | number | undefined,
};

type Row = {
  id: string;
  dateSold: string;
  type: string;
  totalPrice: string;
  commission: string;
  salesRecordingDetailUrl: string | null;
  action: React.ReactNode;
};

const SalesTable = ({
  sales,
  totalCommission,
  totalSales,
}: Props) => {
  const navigate = useNavigate();
  const rows: Row[] = useMemo(() => (sales.map((sale) => {
    const getAction = () => {
      switch (sale.status) {
        case SalesRecordingStatus.InvoicedStatus:
          return <em>Invoiced</em>;
        default:
          return null;
      }
    };

    const getSalesRecordingDetails = () => {
      switch (sale.salesRecordingType) {
        case SalesRecordingType.Adjustment:
        case SalesRecordingType.General:
        case SalesRecordingType.CommissionAfterTravel:
        case SalesRecordingType.PlanningFee:
          return `/trip-matches/${sale.tripMatchId}/sales/${sale.id}`;
        case SalesRecordingType.PartnerRepeat:
        case SalesRecordingType.TravelerReferral:
          return `/sales/repeat-referral/${sale.id}`;
        default:
          return null;
      }
    };

    const salesRecordingDetailUrl = getSalesRecordingDetails();
    const action = getAction();

    return {
      id: sale.id,
      dateSold: formatDateSold(sale.createdAt),
      type: salesRecordingTypeLabel[sale.salesRecordingType],
      totalPrice: new Intl.NumberFormat('en-US', { style: 'currency', currency: sale.price.currencyCode }).format(sale.price.amount),
      commission: new Intl.NumberFormat('en-US', { style: 'currency', currency: sale.commission.currencyCode }).format(sale.commission.amount),
      salesRecordingDetailUrl,
      action,
    };
  })), [sales]);

  const handleRowClick = (row: Row) => () => {
    if (row.salesRecordingDetailUrl) {
      navigate(row.salesRecordingDetailUrl);
    }
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Recorded</TableCell>
            <TableCell>Type</TableCell>
            <TableCell />
            <TableCell />
            <TableCell>Amount</TableCell>
            <TableCell>Commission</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              hover
              key={row.id}
              onClick={handleRowClick(row)}
              sx={{
                cursor: 'pointer',
              }}
            >
              <TableCell>{row.dateSold}</TableCell>
              <TableCell sx={{ minWidth: '130px' }}>{row.type}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell sx={{ color: row.totalPrice.startsWith('-') ? 'error.main' : undefined }}>{row.totalPrice}</TableCell>
              <TableCell sx={{ color: row.commission.startsWith('-') ? 'error.main' : undefined }}>{row.commission}</TableCell>
              <TableCell
                sx={{
                  cursor: 'default',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {row.action}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell />
            <TableCell sx={{ fontFamily: 'captionBold' }}>Totals</TableCell>
            <TableCell />
            <TableCell />
            <TableCell sx={{ fontFamily: 'captionBold' }}>{totalSales}</TableCell>
            <TableCell sx={{ fontFamily: 'captionBold' }}>{totalCommission}</TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default SalesTable;
